import {
  Eye,
  Flame,
  Pencil,
  Snowflake,
  Thermometer,
  Trash2,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { DataTable } from '@/shared/components/data-table'
import { ExpandableText } from '@/shared/components/ExpandableText'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar'
import { Button } from '@/shared/components/ui/button'

import { ViewProductsModal } from '../../../../shared/components/ViewProductsModal'
import { LEAD_WARMTH, PAGE_SIZE } from '../../constants/leads'

import { SourceBadge } from './SourceBadge'

function getInitials(name = '') {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

function WarmthIcon({ warmth }) {
  if (warmth === LEAD_WARMTH.HOT) {
    return <Flame className="size-3.5 text-orange-500" />
  }

  if (warmth === LEAD_WARMTH.COLD) {
    return <Snowflake className="size-3.5 text-sky-500" />
  }

  if (warmth === LEAD_WARMTH.WARM) {
    return <Thermometer className="size-3.5 text-amber-500" />
  }

  return null
}

function LeadActions({ row, onEdit, onDelete, onViewProducts }) {
  return (
    <div className="flex items-center gap-1.5 sm:gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 rounded-full border-indigo-200 bg-indigo-50/80 px-3 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800"
        aria-label={`View details for ${row.company || row.name}`}
        onClick={() => onViewProducts?.(row)}
      >
        <Eye className="size-3.5" />
        View
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-9 rounded-full sm:size-8"
        aria-label={`Edit ${row.company || row.name}`}
        onClick={() => onEdit?.(row)}
      >
        <Pencil className="size-3.5" />
      </Button>
      <Button
        type="button"
        variant="destructive"
        size="icon"
        className="size-9 rounded-full sm:size-8"
        aria-label={`Delete ${row.company || row.name}`}
        onClick={() => onDelete?.(row)}
      >
        <Trash2 className="size-3.5" />
      </Button>
    </div>
  )
}

function LeadMobileCard({ row, onEdit, onDelete, onViewProducts }) {
  const products = row.products || []
  const firstProduct = products[0]
  const hasMultiple = products.length > 1

  return (
    <article className="rounded-2xl border border-black/[0.06] bg-white p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar className="size-10 shrink-0">
            {row.avatarUrl ? (
              <AvatarImage src={row.avatarUrl} alt={row.company || row.name} />
            ) : null}
            <AvatarFallback className="bg-muted text-xs font-bold text-foreground">
              {getInitials(row.company || row.name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            {/* Company Name + Warmth */}
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-bold text-foreground">
                {row.company || '-'}
              </p>
              <WarmthIcon warmth={row.leadWarmth} />
            </div>
          </div>
        </div>
        <LeadActions
          row={row}
          onEdit={onEdit}
          onDelete={onDelete}
          onViewProducts={onViewProducts}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-black/[0.05] pt-3">
        <SourceBadge source={row.source} />
        <div className="text-right">
          {firstProduct ? (
            <div>
              <div className="flex items-center gap-1.5 justify-end">
                <p className="text-sm font-semibold text-foreground">
                  {firstProduct.product}
                </p>
                {hasMultiple ? (
                  <button
                    type="button"
                    onClick={() => onViewProducts?.(row)}
                    className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 cursor-pointer"
                  >
                    +{products.length - 1} more
                  </button>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                Qty{' '}
                {products.reduce(
                  (sum, item) => sum + (Number(item.quantity) || 0),
                  0,
                )}
                {' · '}₹
                {products
                  .reduce(
                    (sum, item) =>
                      sum +
                      (Number(item.quantity) || 0) * (Number(item.value) || 0),
                    0,
                  )
                  .toLocaleString('en-IN')}
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">-</p>
          )}
        </div>
      </div>
    </article>
  )
}

export function LeadsTable({
  data = [],
  meta,
  search,
  onSearchChange,
  onPageChange,
  onEdit,
  onDelete,
  loading = false,
  emptyMessage = 'No leads match your search.',
}) {
  const [selectedLeadForProducts, setSelectedLeadForProducts] = useState(null)

  const total = meta?.total ?? data.length
  const page = meta?.page ?? 1
  const pageSize = meta?.limit ?? PAGE_SIZE
  const showingCount = data.length

  const handleViewProducts = useCallback((lead) => {
    setSelectedLeadForProducts(lead)
  }, [])

  const columns = useMemo(
    () => [
      {
        id: 'lead',
        header: 'Company',
        cell: (row) => (
          <div className="flex min-w-[180px] items-center gap-3">
            <Avatar className="size-9">
              {row.avatarUrl ? (
                <AvatarImage
                  src={row.avatarUrl}
                  alt={row.company || row.name}
                />
              ) : null}
              <AvatarFallback className="bg-muted text-xs font-bold text-foreground">
                {getInitials(row.company || row.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              {/* Company Name + Warmth */}
              <div className="flex items-center gap-2">
                <h3 className="truncate text-sm font-bold text-foreground">
                  {row.company || '-'}
                </h3>
                <WarmthIcon warmth={row.leadWarmth} />
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'createdAt',
        header: 'Created On',
        cell: (row) => (
          <span className="text-xs font-medium text-slate-600">
            {new Date(row.createdAt).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        ),
      },
      {
        id: 'source',
        header: 'Source',
        cell: (row) => <SourceBadge source={row.source} />,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <span className="capitalize text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-1 rounded-md">
            {row.status?.replace(/_/g, ' ') || 'Open'}
          </span>
        ),
      },
      {
        id: 'product',
        header: 'Product',
        cell: (row) => {
          const products = row.products || []
          if (!products.length) return '-'

          const productsWithDesc = products.filter(
            (p) =>
              typeof p.description === 'string' &&
              p.description.trim().length > 0,
          )
          const productsWithoutDesc = products.filter(
            (p) =>
              !p.description ||
              typeof p.description !== 'string' ||
              p.description.trim().length === 0,
          )
          const isPartialDesc =
            products.length > 1 &&
            productsWithDesc.length > 0 &&
            productsWithoutDesc.length > 0

          const firstProduct = isPartialDesc
            ? productsWithDesc[0]?.product || '-'
            : products[0].product
          const restCount = products.length - 1

          return (
            <div className="flex items-center gap-1.5 max-w-[200px]">
              <ExpandableText
                text={firstProduct}
                maxLength={20}
                expandable={false}
              />
              {restCount > 0 ? (
                <button
                  type="button"
                  onClick={() => handleViewProducts(row)}
                  className="inline-flex shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100 cursor-pointer"
                >
                  +{restCount} more
                </button>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => {
          const products = row.products || []
          const productsWithDesc = products.filter(
            (p) =>
              typeof p.description === 'string' &&
              p.description.trim().length > 0,
          )
          const productsWithoutDesc = products.filter(
            (p) =>
              !p.description ||
              typeof p.description !== 'string' ||
              p.description.trim().length === 0,
          )
          const isPartialDesc =
            products.length > 1 &&
            productsWithDesc.length > 0 &&
            productsWithoutDesc.length > 0

          const sourceList = isPartialDesc ? productsWithDesc : products
          const descriptions = sourceList
            .map((p) =>
              typeof p.description === 'string' ? p.description.trim() : '',
            )
            .filter(Boolean)

          if (!descriptions.length && row.description?.trim()) {
            descriptions.push(row.description.trim())
          }
          if (!descriptions.length)
            return <span className="text-muted-foreground">-</span>

          const firstDesc = descriptions[0]
          const restDescCount = descriptions.length - 1

          return (
            <div className="flex items-center gap-1.5 max-w-[200px]">
              <ExpandableText
                text={firstDesc}
                maxLength={20}
                expandable={false}
              />
              {restDescCount > 0 && (
                <button
                  type="button"
                  onClick={() => handleViewProducts(row)}
                  className="inline-flex shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100 cursor-pointer"
                >
                  +{restDescCount} more
                </button>
              )}
            </div>
          )
        },
      },
      {
        id: 'quantity',
        header: 'Quantity',
        headerClassName: 'text-center',
        className: 'text-center',
        cell: (row) => {
          const products = row.products || []
          if (!products.length) return '-'
          const totalQty = products.reduce(
            (sum, item) => sum + (Number(item.quantity) || 0),
            0,
          )

          return (
            <span className="text-sm font-semibold text-foreground">
              {totalQty}
            </span>
          )
        },
      },
      {
        id: 'value',
        header: 'Value',
        headerClassName: 'text-center',
        className: 'text-center',
        cell: (row) => {
          const products = row.products || []
          if (!products.length) return '-'
          const totalValue = products.reduce(
            (sum, item) =>
              sum + (Number(item.quantity) || 0) * (Number(item.value) || 0),
            0,
          )

          return (
            <span className="text-sm font-semibold text-foreground">
              ₹{totalValue.toLocaleString('en-IN')}
            </span>
          )
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        headerClassName: 'text-right',
        className: 'text-right',
        cell: (row) => (
          <div className="flex justify-end">
            <LeadActions
              row={row}
              onEdit={onEdit}
              onDelete={onDelete}
              onViewProducts={handleViewProducts}
            />
          </div>
        ),
      },
    ],
    [handleViewProducts, onDelete, onEdit],
  )

  const renderMobileCard = useCallback(
    (row) => (
      <LeadMobileCard
        row={row}
        onEdit={onEdit}
        onDelete={onDelete}
        onViewProducts={handleViewProducts}
      />
    ),
    [handleViewProducts, onDelete, onEdit],
  )

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        loading={loading}
        search={{
          value: search,
          onChange: onSearchChange,
          placeholder: 'Search leads by name, company, phone...',
        }}
        filterSummary={`${showingCount} of ${total}`}
        pagination={{
          page,
          pageSize,
          total,
          onPageChange,
        }}
        emptyMessage={emptyMessage}
        tableClassName="min-w-[820px]"
        mobileCard={renderMobileCard}
      />

      <ViewProductsModal
        open={Boolean(selectedLeadForProducts)}
        onOpenChange={(open) => !open && setSelectedLeadForProducts(null)}
        lead={selectedLeadForProducts}
      />
    </>
  )
}
